import React from 'react';
import { connect } from 'react-redux';

import { Location } from '../../../common/react/Location';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedRevisionHistorySpaceProvider, getRevisionHistoryVizPath, renderRevisionHistorySpace, RevisionHistoryInfo, RevisionHistorySpaceProviderOwnProps } from './RevisionHistorySpaceProvider';

// we don't care about the item for this space because nothing in redux uses it for this space
const revisionSpaceInfo: SpaceInfo = { spaceType: 'revisionhistory', subspaceName: undefined, item: SpaceItemForNonEntitySpace};

export namespace RevisionHistorySpaceUrlUpdater {
  export interface OwnProps extends ReactDirectiveProps {
    readonly urls: SpacesSiteUrls;
    readonly revisionHistoryInfo: RevisionHistoryInfo;
    readonly routeLocationSearch: string;
    readonly renderProvider: (props: ContainerProps) => JSX.Element;
  }

  interface StateProps {
    readonly vizPath: string;
  }

  export type ContainerProps = OwnProps & StateProps;

  export class Container extends React.PureComponent<ContainerProps> {
    public render() {
      return this.props.renderProvider(this.props);
    }

    public componentDidUpdate(prevProps: ContainerProps) {
      const vizPathFromUrl = getRevisionHistoryVizPath(this.props.revisionHistoryInfo);
      if (this.props.vizPath !== VizPathInitialState &&
        this.props.vizPath !== prevProps.vizPath &&
        this.props.vizPath !== vizPathFromUrl) {
        // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the previous app state vizPath-- see tbViz),
        // so update the url
        const newVizPathSplitOnSlash = this.props.vizPath.split('/');

        // the vizPath is workbookUrlId/sheetUrlId/history/4 where 4 is the revision number
        const newWorkbookUrlId = newVizPathSplitOnSlash[0];
        const newSheetUrlId = newVizPathSplitOnSlash[1];

        if (newSheetUrlId && this.props.revisionHistoryInfo.sheetUrlId) {
          // url only needs updating if the sheet is in the url
          const newRevisionNumber = newVizPathSplitOnSlash[3];

          // routeLocationSearch already includes the ? so just add it to keep the search parameters
          Location.instance.setUrl(this.props.urls.workbookRevisionHistorySpecificSheetUrl({
            workbookUrlId: newWorkbookUrlId,
            revisionNumber: newRevisionNumber,
            sheetUrlId: newSheetUrlId
          }) + this.props.routeLocationSearch);
        }
      }
    }
  }

  const mapStateToProps: MapStateToProps<StateProps> = (appState) => ({
    vizPath: selectVizPath(appState),
  });

  export const ConnectedContainer: React.ComponentClass<OwnProps>
    = connect(mapStateToProps)(Container);

  export const renderProvider = (props: ContainerProps) => {
    const renderRevisionHistorySpaceProps: RevisionHistorySpaceProviderOwnProps = {
      revisionHistoryInfo: props.revisionHistoryInfo,
      routeLocationSearch: props.routeLocationSearch,
      urls: props.urls,
      angularContext: props.angularContext,
      renderSpace: renderRevisionHistorySpace
    };

    return (<ConnectedSpaceInfoProvider spaceInfo={revisionSpaceInfo}>
      <ConnectedRevisionHistorySpaceProvider {...renderRevisionHistorySpaceProps} />
    </ConnectedSpaceInfoProvider>);
  };
}
